import { Injectable, inject } from '@angular/core';
import {environment} from '../../environments/environment';
import {catchError, delay, map, Observable, shareReplay, tap} from 'rxjs';
import {HttpClient, HttpContext} from '@angular/common/http';
import {RAW_HTTP_ERROR} from '../interceptors/auth.interceptor';
import {Address, Business, BusinessData, BusinessUpdateReq, CreatedId, GeoLocation, ListResp} from '../model/store';
import {StorageService} from './storage.service';
import {handleError} from "./handle-error";
import {phoneForWire} from "../components/onboarding/phone";

// Shape matches odin moneta CreateBusinessRequest / BusinessResponse.
// Address field-name mapping (frontend → odin):
//   addressLine1 → line1, addressLine2 → line2, zipcode → postalCode.
export interface OdinAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state?: string | null;
  postalCode?: string | null;
  country: string;
}

export interface CreateBusinessOdinRequest {
  name: string;
  legalName?: string | null;
  taxId?: string | null;
  /** the client's public site. odin stores it since 2026-09-03 */
  website?: string | null;
  email: string;
  phone?: string | null;
  /** required by odin (schema-on-write, 2026-08-31): every billed business has one */
  billingAddress: OdinAddress;
  /** IANA zone, required by odin; never defaulted server-side */
  timezone: string;
  businessType?: 'NAIL_SALON' | 'HAIR_SALON';
  /** SALON unless said otherwise; odin refuses FNB until F&B sub-types exist (2026-09-04) */
  vertical?: 'SALON' | 'FNB';
  /** FT's own account (demo salon, smoke test). Set at creation only; odin never changes it */
  internal?: boolean;
}

// Mirrors odin moneta UpdateBusinessRequest (partial — omitted fields keep value).
export interface UpdateBusinessOdinRequest {
  name?: string;
  legalName?: string | null;
  taxId?: string | null;
  website?: string | null;
  email?: string;
  phone?: string | null;
  billingAddress?: OdinAddress | null;
  timezone?: string;
  businessType?: 'NAIL_SALON' | 'HAIR_SALON';
  /** settable until provisioned; odin answers 409 after */
  vertical?: 'SALON' | 'FNB';
}

// Odin business ids are UUIDs; mercury ids are Firestore strings. Until the
// mercury<->odin id mapping exists (moneta phase 2), the id shape decides
// which backend a business lives in.
export function isOdinBusinessId(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

export function odinAddressToAddress(a: OdinAddress | null | undefined): Address {
  const addr = new Address();
  addr.geoLocation = new GeoLocation(0, 0);
  addr.addressLine1 = a?.line1 ?? '';
  addr.addressLine2 = a?.line2 ?? '';
  addr.city = a?.city ?? '';
  addr.state = a?.state ?? '';
  addr.zipcode = a?.postalCode ?? '';
  addr.country = a?.country ?? '';
  return addr;
}

export interface BusinessOdinResponse {
  id: string;
  name: string;
  legalName?: string | null;
  taxId?: string | null;
  website?: string | null;
  email: string;
  phone?: string | null;
  billingAddress?: OdinAddress | null;
  timezone: string;
  createdAt: string;
  businessType?: string;
  /** SALON | FNB (odin 2026-09-04) */
  vertical?: string;
  /** FT's own account: badge it everywhere, keep it out of every business metric */
  internal?: boolean;
  // plan T1: 9-digit Luhn id moneta mints at creation. null until odin ships it / backfill.
  accountId?: string | null;
  /**
   * ONE lifecycle (odin 2026-09-04): the Go Live card the client is on,
   * INTAKE → PENDING_REVIEW → APPROVED → PROVISIONED → LIVE ⇄ SUSPENDED, any → CLOSED.
   * The old `accountStatus` axis is gone; INTAKE is stored, not derived. Moneta is the
   * authority and the suppliers follow what it publishes. CLOSED is terminal and says why.
   */
  status?: OdinBusinessStatus;
  statusChangedAt?: string | null;
  /** bumps on every status change; mercury holds the same number once it has applied the event */
  statusVersion?: number;
  closedReason?: string | null;
  closedFromStatus?: string | null;
  /**
   * Billing anchors, and the provisioned/live signal. Mercury's primary key for this
   * business IS `id`, so there is no separate mercury reference: provisionedAt answers
   * "does mercury know this client yet".
   */
  provisionedAt?: string | null;
  liveAt?: string | null;
}

export type OdinBusinessStatus =
  'INTAKE' | 'PENDING_REVIEW' | 'APPROVED' | 'PROVISIONED' | 'LIVE' | 'SUSPENDED' | 'CLOSED';
export const ODIN_BUSINESS_STATUSES: readonly OdinBusinessStatus[] =
  ['INTAKE', 'PENDING_REVIEW', 'APPROVED', 'PROVISIONED', 'LIVE', 'SUSPENDED', 'CLOSED'];

/**
 * a location's lifecycle (fianna ft/platform/client-lifecycle.md Table 2.2). INTAKE is moneta's
 * only: mercury never hears of it. CLOSED is terminal, a reopen is a new location.
 */
export type OdinLocationStatus = 'INTAKE' | 'PROVISIONED' | 'ACTIVE' | 'SUSPENDED' | 'CLOSED';

/** why a client or a location was closed; odin requires one on `:close` */
export type OdinClosedReason = 'LOST_BEFORE_LIVE' | 'CHURNED' | 'NON_PAYMENT' | 'FRAUD' | 'MERGED' | 'OTHER';
export const ODIN_CLOSED_REASONS: readonly { value: OdinClosedReason; label: string }[] = [
  { value: 'CHURNED', label: 'Churned' },
  { value: 'LOST_BEFORE_LIVE', label: 'Lost before go-live' },
  { value: 'NON_PAYMENT', label: 'Non-payment' },
  { value: 'FRAUD', label: 'Fraud' },
  { value: 'MERGED', label: 'Merged into another client' },
  { value: 'OTHER', label: 'Other' },
];

@Injectable({
  providedIn: 'root'
})
export class BusinessService {
  private http = inject(HttpClient);
  private storageService = inject(StorageService);

  cachedBusinessId = '';
  $business: Observable<Business | null> | null = null;

  // Odin moneta — POST /moneta/v1/businesses
  createBusinessOnOdin(req: CreateBusinessOdinRequest): Observable<BusinessOdinResponse> {
    return this.http.post<BusinessOdinResponse>(`${this.getMonetaUrl()}/businesses`, req).pipe(
      tap(d => console.log('createBusinessOnOdin', d))
    );
  }

  private getMonetaUrl() {
    return environment.baseUrl + '/moneta/v1';
  }

  // Temporary bridge (moneta phase 2 concern 3): the list reads odin so
  // onboarding-created businesses are visible. Mercury businesses do not appear here.
  // `q` = odin's search: substring on name / legal name; digits against the phone (formatting stripped) or the account ID as a prefix.
  getBusinesses(q = '', status?: OdinBusinessStatus, internal?: boolean): Observable<ListResp<Business> | null> {
    const params: Record<string, string | number | boolean> = {limit: 100};
    if (q.trim()) params['q'] = q.trim();
    if (status) params['status'] = status;
    if (internal !== undefined) params['internal'] = internal;
    return this.http.get<{ items: BusinessOdinResponse[] }>(`${this.getMonetaUrl()}/businesses`, {params}).pipe(
      tap(d => console.log('getBusinesses (odin)', d)),
      map(d => {
        const resp = new ListResp<Business>();
        resp.data = (d?.items ?? []).map(b => this.mapOdinBusiness(b));
        return resp;
      }),
      catchError(handleError<ListResp<Business> | null>("getBusinesses"))
    )
  }

  getBusinessesFromMercury(zipcode: string | null = null): Observable<ListResp<Business> | null> {
    const url = this.getMercuryAdminUrl() + '/businesses'
    const param: any = {};
    if (zipcode != null && zipcode != '') {
      console.log('zipcode', zipcode);
      param['zipcode'] = zipcode;
    }
    console.log('getBusinessesFromMercury', url, param)
    return this.http.get<ListResp<Business>>(url, {params: param}).pipe(
      tap(d => {
        console.log('getAllBusinesses', d)
      }),
      catchError(handleError<ListResp<Business> | null>("getAllBusinesses"))
    )
  }

  /**
   * @deprecated mercury v1 business create. Go Live provisions through
   * `MercuryOnboardingV3Service.onboardClient` since 2026-08-28 (plan T1.2 #16). kept
   * for the mercury-native create flow; delete with the v1 retirement ticket.
   */
  createBusiness(businessData: BusinessData): Observable<CreatedId | null> {
    return this.http.post<CreatedId>(this.getMercuryUrl() + '/businesses', businessData).pipe(
      tap(d => {
        console.log('createBusiness', d)
      }),
      catchError(handleError<CreatedId | null>("createBusiness"))
    )
  }

  // Routed by id shape (temporary bridge, moneta phase 2 concern 3). `website` now
  // reaches odin (2026-09-03); `employerIdNum` still does not, because it overlaps
  // taxId and the decision was to drop it from the UI rather than add a column.
  updateBusiness(businessId: string, businessUpdateReq: BusinessUpdateReq): Observable<Business | null> {
    // One canonicalization for BOTH backends (customer-flow.md Contract 5): odin and the mercury
    // admin route store the same fleet key. A copy — the caller's request object is not ours to edit.
    businessUpdateReq = businessUpdateReq.phoneNumber === undefined
      ? businessUpdateReq
      : {...businessUpdateReq, phoneNumber: phoneForWire(businessUpdateReq.phoneNumber) ?? ''}
    if (isOdinBusinessId(businessId)) {
      return this.http.put<BusinessOdinResponse>(
        `${this.getMonetaUrl()}/businesses/${businessId}`,
        this.toOdinUpdateRequest(businessUpdateReq)
      ).pipe(
        tap(d => console.log('updateBusiness (odin)', d)),
        map(d => this.mapOdinBusiness(d)),
        catchError(handleError<Business | null>("updateBusiness"))
      )
    }
    return this.http.put<Business>(`${this.getMercuryAdminUrl()}/businesses/${businessId}`, businessUpdateReq).pipe(
      tap(d => {
        console.log('updateBusiness', d)
      }),
      catchError(handleError<Business | null>("updateBusiness"))
    )
  }

  private toOdinUpdateRequest(req: BusinessUpdateReq): UpdateBusinessOdinRequest {
    const odinReq: UpdateBusinessOdinRequest = {};
    if (req.name !== undefined) odinReq.name = req.name;
    if (req.phoneNumber !== undefined) odinReq.phone = req.phoneNumber;
    if (req.email !== undefined) odinReq.email = req.email;
    if (req.taxCode !== undefined) odinReq.taxId = req.taxCode;
    if (req.website !== undefined) odinReq.website = req.website || null;
    if (req.timezone !== undefined) odinReq.timezone = req.timezone;
    if (req.legalName !== undefined) odinReq.legalName = req.legalName;
    if (req.businessType !== undefined) odinReq.businessType = req.businessType as UpdateBusinessOdinRequest['businessType'];
    if (req.address !== undefined) {
      odinReq.billingAddress = {
        line1: req.address.addressLine1 ?? '',
        line2: req.address.addressLine2 || null,
        city: req.address.city ?? '',
        state: req.address.state || null,
        postalCode: req.address.zipcode || null,
        country: countryCode(req.address.country),
      };
    }
    return odinReq;
  }

  /**
   * Drop the cached read, so the next getBusinessById asks the server again. A record that
   * writes the client (approve, suspend, close) must call this or it re-renders the body it
   * was already holding: shareReplay keeps answering with the state before the write.
   */
  invalidate(): void {
    this.cachedBusinessId = '';
    this.$business = null;
  }

  getBusinessById(id: string): Observable<Business | null> {
    if (this.cachedBusinessId !== id || !this.$business) {
      this.cachedBusinessId = id
      const source = isOdinBusinessId(id)
        ? this.fetchBusinessById(id)
        : this.fetchBusinessByIdFromMercury(id);
      this.$business = source.pipe(shareReplay(1))
    }

    return this.$business.pipe(delay(1))
  }

  // Odin read path, used for UUID ids (see isOdinBusinessId). The odin response
  // shape differs from the legacy Business model (phone vs phoneNumber,
  // billingAddress vs address, taxId vs taxCode, etc.) so we map it back into
  // Business so every existing consumer keeps working unchanged. Temporary
  // routing until the mercury<->odin id mapping exists (moneta phase 2).
  /**
   * Odin business, unmapped. For the fields the legacy `Business` shape drops
   * (`legalName`, `accountId`, `billingAddress`) — go-live's v3 onboard body needs them.
   * Errors are raw: the caller shows the reason.
   */
  getBusinessOdinById(id: string): Observable<BusinessOdinResponse> {
    return this.http.get<BusinessOdinResponse>(`${this.getMonetaUrl()}/businesses/${id}`);
  }

  fetchBusinessById(id: string): Observable<Business | null> {
    return this.http.get<BusinessOdinResponse>(`${this.getMonetaUrl()}/businesses/${id}`).pipe(
      tap(d => console.log('fetchBusinessById (odin)', d)),
      map(d => this.mapOdinBusiness(d)),
      catchError(handleError<Business | null>("fetchBusinessById"))
    )
  }

  fetchBusinessByIdFromMercury(id: string): Observable<Business | null> {
    return this.http.get<Business>(`${this.getMercuryAdminUrl()}/businesses/${id}`).pipe(
      tap(d => console.log('fetchBusinessByIdFromMercury', d)),
      catchError(handleError<Business | null>("fetchBusinessByIdFromMercury"))
    )
  }

  /**
   * Operating lifecycle (odin 2026-09-03). Moneta is the authority: mercury, chronos and
   * hermes follow what it publishes, so these are the only way the status moves. CLOSED
   * is terminal, which is why there is no inverse of `close`. Re-declaring the current
   * status is a 409 rather than a silent no-op, so the caller learns nothing changed.
   */
  suspendBusiness(businessId: string): Observable<BusinessOdinResponse> {
    return this.http.post<BusinessOdinResponse>(`${this.getMonetaUrl()}/businesses/${businessId}:suspend`, {}, this.rawErrors());
  }

  reactivateBusiness(businessId: string): Observable<BusinessOdinResponse> {
    return this.http.post<BusinessOdinResponse>(`${this.getMonetaUrl()}/businesses/${businessId}:reactivate`, {}, this.rawErrors());
  }

  /** CLOSED asks why: the CFO's re-acquisition list is the consumer of the reason */
  closeBusiness(businessId: string, closedReason: OdinClosedReason): Observable<BusinessOdinResponse> {
    return this.http.post<BusinessOdinResponse>(`${this.getMonetaUrl()}/businesses/${businessId}:close`, { closedReason }, this.rawErrors());
  }

  /** the status verbs answer 409 when nothing changed; the caller reads the status, so the interceptor must not flatten it */
  private rawErrors() {
    return { context: new HttpContext().set(RAW_HTTP_ERROR, true) };
  }

  private mapOdinBusiness(d: BusinessOdinResponse): Business {
    const b = new Business();
    b.id = d.id;
    b.name = d.name ?? '';
    b.phoneNumber = d.phone ?? '';
    b.email = d.email ?? '';
    b.taxCode = d.taxId ?? '';
    b.legalName = d.legalName ?? '';
    b.businessType = d.businessType;
    b.employerIdNum = '';   // odin has no column; taxId carries the tax identity
    b.website = d.website ?? '';
    b.timezone = d.timezone ?? '';
    b.status = d.status;
    b.statusChangedAt = d.statusChangedAt ?? null;
    // one lifecycle since 2026-09-04: `status` alone says where the client is (INTAKE means
    // the intake was left half-done). The anchors ride along for the Go Live tab.
    b.internal = d.internal ?? false;
    b.vertical = d.vertical;
    b.closedReason = d.closedReason ?? null;
    b.closedFromStatus = d.closedFromStatus ?? null;
    b.provisionedAt = d.provisionedAt ?? null;
    b.liveAt = d.liveAt ?? null;
    b.address = odinAddressToAddress(d.billingAddress);
    return b;
  }

  getMercuryAdminUrl() {
    console.log(environment.baseUrl)
    return environment.baseUrl + '/mercury/admin/v1';
  }

  getMercuryUrl() {
    return environment.baseUrl + '/mercury/v1';
  }
}

/**
 * The address country as odin takes it: an ISO 3166-1 alpha-2 string ("US", "CA"). The Edit form's
 * country control holds a `Country` object, and sending that object answered 400 (FT-219 C1 row 2).
 */
function countryCode(country: unknown): string {
  if (typeof country === 'string') return country;
  return (country as { abbreviation?: string } | null)?.abbreviation ?? '';
}
